import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { publicMethods, baseUrl } from '../globals';
import { AuthenticationService } from './authentication.service';
import { Observable } from 'rxjs/internal/Observable';
import {
  catchError,
  finalize,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';
import { of } from 'rxjs';

@Injectable()
export class CRMIntegrationsService {
  constructor(
    private http: HttpClient,
    private pubMethods: publicMethods,
    private auth: AuthenticationService
  ) {}

  wizardLoading = signal({
    isLoadingWizardStep: false,
    isLoadingWizardStepAction: false,
    isLoadingExporter: false,
  });

  isLoading = {
    isLoadingCrmConfig: false,
    isLoadingCrmModule: false,
    isLoadingSuperuserCrmSettings: false,
    isLoadingSalesforceExport: false,
    isLoadingRevokeHubspotAccess: false,
    isLoadingActivateHubspot: false,
    isLoadingHubspotZintMappingOptions: false,
    isDeletingHubspotZintMappingOptions: false,
    isLoadingHubspotImportSettings: false,
    isLoadingHubspotFieldsForImportConfig: false,
    isLoadingDeleteHubSpotFieldMapping: false,
    isLoadingCreateNewImportConfiguration: false,
  };

  wizardStepsReadyState = signal({});
  configCRMType = signal('');
  configCRMSelectedObjectId = signal(null);
  specificConfigWizardStep = signal(null);
  cachedCRMObjectFields$: Observable<any> | null = null;

  buildCRMWizardEndpoint({
    stepType = '',
    actionType = '',
    objectId = null,
    fieldId = null,
    permissionsUpdateType = '',
    qParam = '',
    crmName = '',
  }): string {
    const withOptionalId = (
      base: string,
      objectId?: number | null,
      fieldId?: number | null
    ): string => {
      if (objectId && fieldId !== undefined) {
        /* NB: must pass NULL when no fieldId e.g for /object/{crmObjectPk}/field/
        * withOptionalId(`/object/`, 8, 400) => /object/8/field/400/
          withOptionalId(`/object/`, 8, null) => /object/8/field/
          withOptionalId(`/object/`, 8) => /object/8/
        */
        return fieldId
          ? `${base}${objectId}/field/${fieldId}/`
          : `${base}${objectId}/field/`;
      }

      return objectId ? `${base}${objectId}/` : base;
    };

    const getPermissionsEndpoint = () => {
      /**
       * NB: objectId passed here is NOT the usual crmObjectId
       * it's actually exporterPermissionsId!
       */
      const basePath = `/crm_framework/wizard/${this.configCRMType()}/export_permissions/`;

      let restOfPath = '';
      if (objectId || permissionsUpdateType) {
        const exporterPermissionsId = objectId;
        restOfPath =
          `${exporterPermissionsId}/` +
          (permissionsUpdateType ? `${permissionsUpdateType}/` : '');
      } else {
        restOfPath = 'list_users_and_orgs/';
      }

      return `${basePath}${restOfPath}`;
    };

    const wizardActionToEndpoint = {
      get_auth_url: `/crm_framework/wizard/${this.configCRMType()}/${stepType}/get_auth_url/`,
      list_crm_object_types: `/crm_framework/crms/${this.configCRMType()}/object/list_crm_object_types/`,
      list_crm_object_fields: `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/field/list_crm_field_types/${qParam || ''}`,
      list_zint_options: `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/export_mapping/available_options/`,
      list_custom_columns: `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/import_mapping/list_compatible_custom_columns/${qParam || ''}`,
      list_all_transformers: `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/transform/available_lookup_transformers/`,
      transform: withOptionalId(
        `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/transform/`,
        fieldId
      ),
      export_mappings: `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/export_mapping/`,
      export_permissions: getPermissionsEndpoint(),
      crm_object_type: withOptionalId(
        `/crm_framework/crms/${crmName || this.configCRMType()}/object/`,
        objectId
      ),
      crm_object_fields: withOptionalId(
        `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/field/`,
        fieldId
      ),
      import_mappings: withOptionalId(
        `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/import_mapping/`,
        fieldId
      ),
      list_sync_logs: `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/log/`,
      enrichment_summary: `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/enrichment_summary/`,
      list_matcher_field_types: `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/matcher_field/list_matcher_field_types/`,
      matcher_fields: fieldId
        ? `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/matcher_field/${fieldId}/`
        : `/crm_framework/crms/${this.configCRMType()}/object/${objectId}/matcher_field/`,
    };

    if (actionType) {
      return baseUrl + wizardActionToEndpoint[actionType];
    }

    const stepPath = `/crm_framework/wizard/${this.configCRMType()}/${stepType}/step/`;

    return baseUrl + stepPath;
  }

  updateLoadingSignal(flag: string, loadingState: boolean): void {
    this.wizardLoading.update(prev => ({ ...prev, [flag]: loadingState }));
  }

  getConfigsForCRM(crmType: string): Observable<any> {
    this.isLoading.isLoadingCrmConfig = true;

    return this.http
      .get(
        baseUrl + `/crm_framework/wizard/${crmType}/`,
        this.auth.helperGetVerifiedGetParams()
      )
      .pipe(
        tap(data => {
          this.configCRMType.set(data['crmType']);
          data['steps'].forEach((step: Record<string, any>) => {
            this.wizardStepsReadyState.update(obj => ({
              ...obj,
              [step.stepType]: step['isReady'],
            }));
          });
        }),
        finalize(() => (this.isLoading.isLoadingCrmConfig = false)),
        catchError(
          this.pubMethods.handleError(
            'getConfigsForCRMType',
            [],
            this.isLoading
          )
        )
      );
  }

  getAvailableCRMs(): Observable<any> {
    this.isLoading.isLoadingCrmModule = true;
    return this.http
      .get(
        baseUrl + '/crm_framework/crms/',
        this.auth.helperGetVerifiedGetParams()
      )
      .pipe(
        finalize(() => {
          this.isLoading.isLoadingCrmModule = false;
        }),
        catchError(
          this.pubMethods.handleError('getAvailableCRMs', [], this.isLoading)
        )
      );
  }

  markConfigStepCompleted(stepType = '', completed = true): Observable<any> {
    this.updateLoadingSignal('isLoadingWizardStep', true);
    const param = { isCompleted: completed };
    return this.http
      .post(
        baseUrl +
          `/crm_framework/wizard/${this.configCRMType()}/${stepType}/step/`,
        param,
        this.auth.helperGetVerifiedPostDict(true)
      )
      .pipe(
        catchError(
          this.pubMethods.handleError(
            'markConfigStepCompleted',
            [],
            this.isLoading
          )
        ),
        finalize(() => {
          this.updateLoadingSignal('isLoadingWizardStep', false);
        })
      );
  }

  getWizardStepStatus(stepType?: string): Observable<any> {
    this.updateLoadingSignal('isLoadingWizardStep', true);
    const url = this.buildCRMWizardEndpoint({ stepType });

    return this.http.get(url, this.auth.helperGetVerifiedGetParams()).pipe(
      finalize(() => this.updateLoadingSignal('isLoadingWizardStep', false)),
      catchError(
        this.pubMethods.handleError('getWizardStepStatus', [], this.isLoading)
      )
    );
  }

  // NB: wrapper method for getWizardStepStatus to avoid repeated subscriptions
  getCRMConfigStepStatus(stepType: string): void {
    this.getWizardStepStatus(stepType).subscribe(data => {
      this.wizardStepsReadyState.update(state => {
        return { ...state, [stepType]: data?.isReady };
      });
    });
  }

  getCRMExistingState({
    stepType,
    actionType,
    objectId,
    fieldId,
    crmName,
  }: {
    stepType?: string;
    actionType?: string;
    objectId?: number;
    fieldId?: number;
    crmName?: string;
  }): Observable<any> {
    this.updateLoadingSignal('isLoadingWizardStepAction', true);
    let url = this.buildCRMWizardEndpoint({
      stepType,
      actionType,
      objectId,
      fieldId,
      crmName,
    });

    return this.http.get(url, this.auth.helperGetVerifiedGetParams()).pipe(
      finalize(() => {
        this.updateLoadingSignal('isLoadingWizardStepAction', false);
      }),
      catchError(
        this.pubMethods.handleError('getCRMExistingState', [], this.isLoading)
      )
    );
  }

  getCRMStepActionContent({
    stepType,
    actionType,
    objectId,
    refetch = false,
    qParam,
  }: {
    stepType: string;
    actionType: string;
    objectId?: number;
    refetch?: boolean;
    qParam?: string;
  }): Observable<any> {
    this.updateLoadingSignal('isLoadingWizardStepAction', true);

    if (actionType === 'list_crm_object_fields') {
      if (refetch === false && this.cachedCRMObjectFields$) {
        this.updateLoadingSignal('isLoadingWizardStepAction', false);
        return this.cachedCRMObjectFields$;
      }
    }

    const url = this.buildCRMWizardEndpoint({
      stepType,
      actionType,
      objectId,
      qParam,
    });

    const crmActionContent$ = this.http
      .get(url, this.auth.helperGetVerifiedGetParams())
      .pipe(
        finalize(() =>
          this.updateLoadingSignal('isLoadingWizardStepAction', false)
        ),
        catchError(
          this.pubMethods.handleError(
            'getCRMStepActionContent',
            [],
            this.isLoading
          )
        ),
        shareReplay() //for cache
      );

    if (actionType === 'list_crm_object_fields' && !qParam) {
      // only cache if NO qParam: ?zint_field_type=Text
      this.cachedCRMObjectFields$ = crmActionContent$;
    }

    return crmActionContent$;
  }

  postCRMStepActionJSON({
    postActionType,
    params,
    objectId,
    permissionsUpdateType,
  }: {
    postActionType: string;
    params: Record<string, any>;
    objectId?: number;
    permissionsUpdateType?: string;
  }): Observable<any> {
    this.updateLoadingSignal('isLoadingWizardStepAction', true);
    const url = this.buildCRMWizardEndpoint({
      actionType: postActionType,
      objectId,
      permissionsUpdateType,
    });

    return this.http
      .post(url, params, {
        ...this.auth.helperGetVerifiedPostDict(true),
        observe: 'response',
      })
      .pipe(
        finalize(() =>
          this.updateLoadingSignal('isLoadingWizardStepAction', false)
        ),
        catchError(error => {
          this.pubMethods.handleError(
            'postCRMStepActionContent',
            [],
            this.isLoading
          )(error);
          return of({ error });
        })
      );
  }

  updateCRMStepActionContent({
    updateActionType,
    params,
    objectId,
    fieldId,
    updateMethod = 'put',
  }: {
    updateActionType: string;
    params?: Record<string, any>;
    objectId: number;
    fieldId?: number;
    updateMethod?: 'put' | 'patch' | 'delete';
  }): Observable<any> {
    this.updateLoadingSignal('isLoadingWizardStepAction', true);
    const url = this.buildCRMWizardEndpoint({
      actionType: updateActionType,
      objectId,
      fieldId,
    });

    let updateOb$: Observable<any> = of(null);

    if (updateMethod === 'delete') {
      updateOb$ = this.http.delete(url, {
        ...this.auth.helperGetVerifiedDeleteDict(objectId),
        observe: 'response',
      });
    } else {
      // put or patch with params
      updateOb$ = this.http[updateMethod](url, params, {
        ...this.auth.helperGetVerifiedPutPatchDict(),
        observe: 'response',
      });
    }

    return updateOb$.pipe(
      finalize(() =>
        this.updateLoadingSignal('isLoadingWizardStepAction', false)
      ),
      catchError(
        this.pubMethods.handleError(
          'putOrPatchCRMStepActionContent',
          [],
          this.isLoading
        )
      )
    );
  }

  deleteCRMRecordObject(
    deleteActionType: string,
    objectId: number
  ): Observable<any> {
    this.updateLoadingSignal('isLoadingWizardStepAction', true);
    const url = this.buildCRMWizardEndpoint({
      actionType: deleteActionType,
      objectId,
    });
    return this.http
      .delete(url, {
        ...this.auth.helperGetVerifiedDeleteDict(objectId),
        observe: 'response',
      })
      .pipe(
        finalize(() =>
          this.updateLoadingSignal('isLoadingWizardStepAction', false)
        ),
        catchError(
          this.pubMethods.handleError(
            'deleteCRMRecordObject',
            [],
            this.isLoading
          )
        )
      );
  }

  deleteConfigStep(stepType = ''): Observable<any> {
    /*NB: use only when CRM object changes
     * no catchError as we expect some 404s here
     */
    return this.http
      .delete(
        baseUrl +
          `/crm_framework/wizard/${this.configCRMType()}/${stepType}/step`,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(catchError(error => of([])));
  }

  updateCRMObject(objectId: number, params: Record<string, any>): Observable<any> {
    const url = `${baseUrl}/crm_framework/crms/${this.configCRMType()}/object/${objectId}/`;
    return this.http
      .patch(url, params, {
        ...this.auth.helperGetVerifiedPutPatchDict(),
        observe: 'response',
      })
      .pipe(
        catchError(
          this.pubMethods.handleError('updateCRMObject', [], this.isLoading)
        )
      );
  }

  getCRMObjectDetails(objectId: number): Observable<any> {
    const url = `${baseUrl}/crm_framework/crms/${this.configCRMType()}/object/${objectId}/`;
    return this.http.get(url, this.auth.helperGetVerifiedGetParams());
  }

  triggerCompanyMatcher(objectId: number): Observable<any> {
    const url = `${baseUrl}/crm_framework/crms/${this.configCRMType()}/object/${objectId}/trigger_company_matcher/`;
    return this.http.post(url, {}, {
      ...this.auth.helperGetVerifiedPostDict(),
      observe: 'response',
    });
  }

  getCompanyMatcherStatus(objectId: number): Observable<any> {
    const url = `${baseUrl}/crm_framework/crms/${this.configCRMType()}/object/${objectId}/company_matcher_status/`;
    return this.http.get(url, this.auth.helperGetVerifiedGetParams());
  }

  /* ------crm export services-------- */

  getCRMExportSettings({
    objectId,
    crmName,
    companyIds,
    searchQParams,
  }: {
    crmName: string;
    objectId: number;
    companyIds?: string[];
    searchQParams?: Record<string, any>;
  }): Observable<any> {
    this.updateLoadingSignal('isLoadingExporter', true);

    let jsonBody: Record<string, any>;
    if (this.pubMethods.hasData(companyIds)) {
      jsonBody = { companyIds };
    } else if (this.pubMethods.hasData(searchQParams)) {
      jsonBody = searchQParams;
    }

    const url =
      baseUrl +
      `/crm_framework/crms/${crmName.toLowerCase()}/object/${objectId}/export/${companyIds?.length ? 'search_by_company_ids' : 'search_by_query'}/`;

    return this.http
      .post(url, jsonBody, this.auth.helperGetVerifiedPostDict(true))
      .pipe(
        finalize(() => this.updateLoadingSignal('isLoadingExporter', false)),
        catchError(
          this.pubMethods.handleError(
            'getCRMExporterSettings',
            [],
            this.isLoading
          )
        )
      );
  }

  updateCRMRecords({
    objectId,
    crmName,
    payloadObj,
  }: {
    objectId: number;
    crmName: string;
    payloadObj: Record<string, {}>;
  }): Observable<any> {
    if (!this.pubMethods.hasData(payloadObj)) return;

    this.updateLoadingSignal('isLoadingExporter', true);

    const url =
      baseUrl +
      `/crm_framework/crms/${crmName.toLowerCase()}/object/${objectId}/export/update/`;

    return this.http
      .post(url, payloadObj, {
        ...this.auth.helperGetVerifiedPostDict(true),
        observe: 'response',
      })
      .pipe(
        finalize(() => this.updateLoadingSignal('isLoadingExporter', false)),
        catchError(
          this.pubMethods.handleError('updateCRMRecords', [], this.isLoading)
        )
      );
  }

  createNewCRMRecords({
    crmName,
    objectId,
    payloadObj,
  }: {
    crmName: string;
    objectId: number;
    payloadObj: Record<string, string[]>;
  }): Observable<any> {
    if (!this.pubMethods.hasData(payloadObj)) return;

    this.updateLoadingSignal('isLoadingExporter', true);

    const url =
      baseUrl +
      `/crm_framework/crms/${crmName.toLowerCase()}/object/${objectId}/export/create/`;

    return this.http
      .post(url, payloadObj, {
        ...this.auth.helperGetVerifiedPostDict(true),
        observe: 'response',
      })
      .pipe(
        finalize(() => this.updateLoadingSignal('isLoadingExporter', false)),
        catchError(
          this.pubMethods.handleError('updateCRMRecords', [], this.isLoading)
        )
      );
  }

  /* ------existing legacy crm services -------- */
  /* ------existing legacy crm services -------- */
  /* ------existing legacy crm services -------- */
  /* ------existing legacy crm services -------- */
  /* ------existing legacy crm services -------- */

  getCRMIntegrationDetails(): Observable<any> {
    this.isLoading['isLoadingCrmModule'] = true;
    return this.http
      .post(
        baseUrl + '/get_crm_integration_details/',
        {},
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(_ => (this.isLoading['isLoadingCrmModule'] = false)),
        catchError(
          this.pubMethods.handleError(
            'getCRMIntegrationDetails',
            [],
            this.isLoading
          )
        )
      );
  }

  updateUserAccessLevels(teamMemberId, hasAccess): Observable<any> {
    this.isLoading['isLoadingSuperuserCrmSettings'] = true;
    const params = new HttpParams()
      .set('teamMemberId', teamMemberId)
      .set('hasAccess', hasAccess);
    return this.http
      .post(
        baseUrl + '/update_user_salesforce_access_levels/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(_ => (this.isLoading['isLoadingSuperuserCrmSettings'] = false)),
        catchError(
          this.pubMethods.handleError(
            'updateUserAccessLevels',
            [],
            this.isLoading
          )
        )
      );
  }

  changeSalesforceEnableForAllSettings(
    salesforceAutomaticallyEnabledForAll
  ): Observable<any> {
    this.isLoading['isLoadingSuperuserCrmSettings'] = true;
    const params = new HttpParams().set(
      'salesforceAutomaticallyEnabledForAll',
      salesforceAutomaticallyEnabledForAll
    );
    return this.http
      .post(
        baseUrl + '/change_salesforce_enabled_for_all_setting/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(_ => (this.isLoading['isLoadingSuperuserCrmSettings'] = false)),
        catchError(
          this.pubMethods.handleError(
            'changeSalesforceEnableForAllSettings',
            [],
            this.isLoading
          )
        )
      );
  }

  selectSalesforceObject(salesforceObject): Observable<any> {
    this.isLoading['isLoadingSuperuserCrmSettings'] = true;
    const params = new HttpParams().set('salesforceObject', salesforceObject);
    return this.http
      .post(
        baseUrl + '/select_salesforce_object/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(_ => (this.isLoading['isLoadingSuperuserCrmSettings'] = false)),
        catchError(
          this.pubMethods.handleError(
            'selectSalesforceObject',
            [],
            this.isLoading
          )
        )
      );
  }

  saveZintToSalesforceMappings(
    zintCustomColumnsToSalesforceAttributesMapping,
    zintAttributesToSalesforceAttributesMapping
  ): Observable<any> {
    this.isLoading['isLoadingSuperuserCrmSettings'] = true;
    const params = new HttpParams()
      .set(
        'zintCustomColumnsToSalesforceAttributesMapping',
        this.pubMethods.stringifyDictForGetParams(
          zintCustomColumnsToSalesforceAttributesMapping
        )
      )
      .set(
        'zintAttributesToSalesforceAttributesMapping',
        this.pubMethods.stringifyDictForGetParams(
          zintAttributesToSalesforceAttributesMapping
        )
      );
    return this.http
      .post(
        baseUrl + '/save_zint_to_salesforce_mappings/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(_ => (this.isLoading['isLoadingSuperuserCrmSettings'] = false)),
        catchError(
          this.pubMethods.handleError(
            'saveZintToSalesforceMappings',
            [],
            this.isLoading
          )
        )
      );
  }

  addSalesforceAttributeToConstantMapping(
    salesforceAttributeName,
    constantToSet
  ): Observable<any> {
    this.isLoading['isLoadingSuperuserCrmSettings'] = true;
    const params = new HttpParams()
      .set('salesforceAttributeName', salesforceAttributeName)
      .set('constantToSet', constantToSet);
    return this.http
      .post(
        baseUrl + '/add_salesforce_attribute_to_constant_mapping/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(_ => (this.isLoading['isLoadingSuperuserCrmSettings'] = false)),
        catchError(
          this.pubMethods.handleError(
            'addSalesforceAttributeToConstantMapping',
            [],
            this.isLoading
          )
        )
      );
  }

  deleteSalesforceAttributeToConstantMapping(
    salesforceAttributeName
  ): Observable<any> {
    this.isLoading['isLoadingSuperuserCrmSettings'] = true;
    const params = new HttpParams().set(
      'salesforceAttributeName',
      salesforceAttributeName
    );
    return this.http
      .post(
        baseUrl + '/delete_salesforce_attribute_to_constant_mapping/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(_ => (this.isLoading['isLoadingSuperuserCrmSettings'] = false)),
        catchError(
          this.pubMethods.handleError(
            'deleteSalesforceAttributeToConstantMapping',
            [],
            this.isLoading
          )
        )
      );
  }

  saveZintToSalesforceUniqueIdentifierMapping(
    zintAttributeUniqueIdentifier,
    salesforceAttributeUniqueIdentifier
  ): Observable<any> {
    this.isLoading['isLoadingSuperuserCrmSettings'] = true;
    const params = new HttpParams()
      .set('zintAttributeUniqueIdentifier', zintAttributeUniqueIdentifier)
      .set(
        'salesforceAttributeUniqueIdentifier',
        salesforceAttributeUniqueIdentifier
      );
    return this.http
      .post(
        baseUrl + '/save_unique_identifer_mapping/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(_ => (this.isLoading['isLoadingSuperuserCrmSettings'] = false)),
        catchError(
          this.pubMethods.handleError(
            'saveZintToSalesforceUniqueIdentifierMapping',
            [],
            this.isLoading
          )
        )
      );
  }

  exportCompaniesToSalesforcePreCheck(companyIds): Observable<any> {
    this.isLoading['isLoadingSalesforceExport'] = true;
    const params = new HttpParams().set(
      'companyIds',
      this.pubMethods.stringifyListForGetParams(companyIds)
    );
    return this.http
      .post(
        baseUrl + '/pre_check_export_companies_to_salesforce/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(_ => (this.isLoading['isLoadingSalesforceExport'] = false)),
        catchError(
          this.pubMethods.handleError(
            'exportCompaniesToSalesforcePreCheck',
            [],
            this.isLoading
          )
        )
      );
  }

  exportCompaniesToSalesforce(companyIdsToSalesforceIds): Observable<any> {
    this.isLoading['isLoadingSalesforceExport'] = true;
    const params = new HttpParams().set(
      'companyIdsToSalesforceIds',
      this.pubMethods.stringifyDictForGetParams(companyIdsToSalesforceIds)
    );
    return this.http
      .post(
        baseUrl + '/export_companies_to_salesforce/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(_ => (this.isLoading['isLoadingSalesforceExport'] = false)),
        catchError(
          this.pubMethods.handleError(
            'exportCompaniesToSalesforce',
            [],
            this.isLoading
          )
        )
      );
  }

  getHubspotAuthStateAndURL(): Observable<any> {
    this.isLoading['isLoadingHubspotIntegration'] = true;
    const cachedPublicURL: string = sessionStorage.getItem('hs_public_url');
    const params = new HttpParams();
    return this.http
      .post(
        baseUrl + '/hubspot/get_hubspot_state/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        switchMap((response: Record<string, boolean>) => {
          if (response.success === false) {
            //   If NOT success, return the url - either cached or new.
            if (cachedPublicURL) {
              return of({ url: cachedPublicURL });
            } else {
              return this.http
                .post(
                  baseUrl + '/hubspot/oauth/public_app_url/',
                  params,
                  this.auth.helperGetVerifiedPostDict()
                )
                .pipe(
                  tap(response => {
                    sessionStorage.setItem('hs_public_url', response['url']);
                  })
                );
            }
          } else {
            // If success, remove cached url and return response.
            sessionStorage.removeItem('hs_public_url');
            return of(response);
          }
        }),
        tap(_ => (this.isLoading['isLoadingHubspotIntegration'] = false)),
        catchError(
          this.pubMethods.handleError('hubspotIntegration', [], this.isLoading)
        )
      );
  }

  revokeHubspotAccess(): Observable<any> {
    const params = new HttpParams();
    return this.http
      .post(baseUrl + '/hubspot/revoke_hubspot_auth/', params, {
        ...this.auth.helperGetVerifiedPostDict(),
        observe: 'response',
      })
      .pipe(
        tap(_ => (this.isLoading['isLoadingRevokeHubspotAccess'] = false)),
        catchError(
          this.pubMethods.handleError('revokeHubspotAccess', [], this.isLoading)
        )
      );
  }

  activateHubspotIntegration(): Observable<any> {
    const params = new HttpParams();
    this.isLoading['isLoadingActivateHubspot'] = true;
    return this.http
      .post(
        baseUrl + '/hubspot/activate_hubspot_integration/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(_ => (this.isLoading['isLoadingActivateHubspot'] = false)),
        catchError(
          this.pubMethods.handleError(
            'activateHubspotIntegration',
            [],
            this.isLoading
          )
        )
      );
  }

  toggleHubspotEnrichmentProcess(hubspotObjectName): Observable<any> {
    const params = new HttpParams().set('hubspotObjectName', hubspotObjectName);
    this.isLoading['isLoadingToggleHubspotEnrichment'] = true;
    return this.http
      .post(
        baseUrl + '/hubspot/toggle_hubspot_enrichment/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(_ => (this.isLoading['isLoadingToggleHubspotEnrichment'] = false)),
        catchError(
          this.pubMethods.handleError(
            'toggleHubspotEnrichmentProcess',
            [],
            this.isLoading
          )
        )
      );
  }

  toggleHubspotAssignOwnerOnCreate(hubspotObjectName): Observable<any> {
    const params = new HttpParams().set('hubspotObjectName', hubspotObjectName);
    this.isLoading['isLoadingToggleHubspotAssignOwnerOnCreate'] = true;
    return this.http
      .post(
        baseUrl + '/hubspot/toggle_hubspot_assign_owner_on_create/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(
          _ =>
            (this.isLoading['isLoadingToggleHubspotAssignOwnerOnCreate'] =
              false)
        ),
        catchError(
          this.pubMethods.handleError(
            'toggleHubspotAssignOwnerOnCreate',
            [],
            this.isLoading
          )
        )
      );
  }

  getZintAndHubspotMappingOptions(): Observable<any> {
    const params = new HttpParams();
    this.isLoading['isLoadingHubspotZintMappingOptions'] = true;
    return this.http
      .post(
        baseUrl + '/hubspot/get_all_hubspot_and_zint_attributes/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(
          _ => (this.isLoading['isLoadingHubspotZintMappingOptions'] = false)
        ),
        catchError(
          this.pubMethods.handleError(
            'getZintAndHubspotMappingOptions',
            [],
            this.isLoading
          )
        )
      );
  }

  deleteHubspotFieldMapping(fieldMappingId): Observable<any> {
    const params = new HttpParams().set('fieldMappingId', fieldMappingId);
    this.isLoading['isDeletingHubspotZintMappingOptions'] = true;
    return this.http
      .post(
        baseUrl + '/hubspot/delete_hubspot_property_mapping/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(
          _ => (this.isLoading['isDeletingHubspotZintMappingOptions'] = false)
        ),
        catchError(
          this.pubMethods.handleError(
            'deleteHubspotFieldMapping',
            [],
            this.isLoading
          )
        )
      );
  }

  addHubspotPropertyMapping(
    hubspotFieldName,
    zintFieldName,
    shouldOverwrite,
    isLookupField
  ): Observable<any> {
    const params = new HttpParams()
      .set('hubspotFieldName', hubspotFieldName)
      .set(
        'zintFieldName',
        this.pubMethods.stringifyDictForGetParams(zintFieldName)
      )
      .set('shouldOverwrite', shouldOverwrite)
      .set('isLookupField', isLookupField);
    this.isLoading['addingHubspotPropertyMapping'] = true;
    return this.http
      .post(
        baseUrl + '/hubspot/add_hubspot_property_mapping/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(_ => (this.isLoading['addingHubspotPropertyMapping'] = false)),
        catchError(
          this.pubMethods.handleError(
            'addHubspotPropertyMapping',
            [],
            this.isLoading
          )
        )
      );
  }

  // -------------------- Hubspot Import Mappings. The below is used to configure the import of data from HubSpot into Zint. --------------------

  getHubspotImportSettings(): Observable<any> {
    const params = new HttpParams();
    this.isLoading['isLoadingHubspotImportSettings'] = true;
    return this.http
      .post(
        baseUrl + '/hubspot/get_current_hubspot_import_settings/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(_ => (this.isLoading['isLoadingHubspotImportSettings'] = false)),
        catchError(
          this.pubMethods.handleError(
            'getHubspotImportSettings',
            [],
            this.isLoading
          )
        )
      );
  }

  getHubspotFieldsForImportConfig(): Observable<any> {
    const params = new HttpParams();
    this.isLoading['isLoadingHubspotFieldsForImportConfig'] = true;
    return this.http
      .post(
        baseUrl +
          '/hubspot/get_options_for_hubspot_import_config_configuration/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(
          _ => (this.isLoading['isLoadingHubspotFieldsForImportConfig'] = false)
        ),
        catchError(
          this.pubMethods.handleError(
            'getHubspotImportSettings',
            [],
            this.isLoading
          )
        )
      );
  }

  addHubspotImportFieldMapping(
    hubspotObjectImportConfigId,
    hubspotFieldName,
    columnId
  ): Observable<any> {
    const params = new HttpParams()
      .set('hubspotObjectImportConfigId', hubspotObjectImportConfigId)
      .set('hubspotFieldName', hubspotFieldName)
      .set('columnId', columnId);
    this.isLoading['isLoadingHubspotFieldsForImportConfig'] = true;
    return this.http
      .post(
        baseUrl + '/hubspot/add_hubspot_import_field_mapping/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(
          _ => (this.isLoading['isLoadingHubspotFieldsForImportConfig'] = false)
        ),
        catchError(
          this.pubMethods.handleError(
            'getHubspotImportSettings',
            [],
            this.isLoading
          )
        )
      );
  }

  deleteHubspotImportFieldMapping(
    hubspotObjectImportConfigId,
    hubspotFieldMappingId
  ): Observable<any> {
    const params = new HttpParams()
      .set('hubspotObjectImportConfigId', hubspotObjectImportConfigId)
      .set('hubspotImportFieldMappingId', hubspotFieldMappingId);
    this.isLoading['isLoadingDeleteHubSpotFieldMapping'] = true;
    return this.http
      .post(
        baseUrl + '/hubspot/delete_hubspot_import_field_mapping/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(
          _ => (this.isLoading['isLoadingDeleteHubSpotFieldMapping'] = false)
        ),
        catchError(
          this.pubMethods.handleError(
            'deleteHubspotImportFieldMapping',
            [],
            this.isLoading
          )
        )
      );
  }

  createNewHubspotImportConfiguration(): Observable<any> {
    const params = new HttpParams();
    this.isLoading['isLoadingCreateNewImportConfiguration'] = true;
    return this.http
      .post(
        baseUrl + '/hubspot/activate_hubspot_import_config/',
        params,
        this.auth.helperGetVerifiedPostDict()
      )
      .pipe(
        tap(
          _ => (this.isLoading['isLoadingCreateNewImportConfiguration'] = false)
        ),
        catchError(
          this.pubMethods.handleError(
            'createNewHubspotImportConfiguration',
            [],
            this.isLoading
          )
        )
      );
  }
}
